/**
 * ApproveDialog — POST /intake/:id/approve (plan §4.4, §13.4 WP-31 frontend
 * scope): "approve dialog ... cannot submit until study, negotiation,
 * direction, round and date are set."
 */
import { useState, FormEvent } from 'react';
import { Modal, primaryBtnCls, secondaryBtnCls, useToast } from '../../../ui';
import { approveIntakeItem } from '../../api/intake';
import type { ApproveResult, IntakeItem } from '../../api/intake';
import { AssignmentFields, EMPTY_ASSIGNMENT, assignmentIsComplete, assignmentToInput, tagUnresolvedMessage, withTagCandidates } from './AssignmentFields';
import type { AssignmentState } from './AssignmentFields';
import type { TagUnresolvedErrorBody } from '../../api/client';

interface Props {
  item: IntakeItem;
  onClose: () => void;
  onApproved: (result: ApproveResult) => void;
}

export function ApproveDialog({ item, onClose, onApproved }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<AssignmentState>(EMPTY_ASSIGNMENT);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!assignmentIsComplete(state)) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const result = await approveIntakeItem(item.id, assignmentToInput(state));
      toast(`${item.drive_name} approved.`, { type: 'success' });
      onApproved(result);
    } catch (err) {
      // Narrow structurally (no axios import here — this repo's Jest cannot
      // parse axios's ESM build unmocked; every module under src/contracts
      // reaches it only through the mocked services/api, never directly).
      const res = (err as { response?: { status?: number; data?: any } })?.response;
      if (!res) {
        setFormError('Something went wrong approving this item.');
      } else if (res.status === 409 && Array.isArray(res.data?.candidates)) {
        const body = res.data as TagUnresolvedErrorBody;
        setState((prev) => withTagCandidates(prev, body.field, body.candidates));
        setFormError(tagUnresolvedMessage(body.field));
      } else if (typeof res.data?.error === 'string') {
        setFormError(res.data.error);
      } else {
        setFormError('Something went wrong approving this item.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = assignmentIsComplete(state) && !submitting;

  return (
    <Modal isOpen onClose={onClose} title={`Approve — ${item.drive_name}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {formError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {formError}
          </div>
        )}

        <AssignmentFields state={state} onChange={setState} idPrefix="cta-approve" />

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" onClick={onClose} className={secondaryBtnCls}>
            Cancel
          </button>
          <button type="submit" data-testid="cta-approve-submit" disabled={!canSubmit} className={primaryBtnCls}>
            {submitting ? 'Approving…' : 'Approve'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default ApproveDialog;
